import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, 
  CheckCircle2,
  Plus,
  Minus
} from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    document.title = 'About Us | Atlas Defenders';
    window.scrollTo(0, 0);
  }, []);

  const serviceCategories = [
    {
      title: 'IT Infrastructure & Engineering',
      bigTitle: 'Design and deploy resilient digital foundations',
      description: 'We design, deploy, and manage complete IT infrastructures tailored to business needs, from physical implementation to hybrid cloud optimization.',
      image: '/images/Server administration 4.png',
      items: [
        'Data center design and full deployment',
        'Network architecture (LAN, WAN, Wireless)',
        'Enterprise equipment configuration',
        'Cloud and hybrid infrastructure integration',
        'Virtualization and system optimization',
        'Advanced Server administration'
      ]
    },
    {
      title: 'Cybersecurity Services',
      bigTitle: 'Embed security into every layer of your business',
      description: 'Our security-first approach combines defensive strategies with offensive testing and 24/7 monitoring to keep your assets safe.',
      image: '/images/Soc-as-a-service2.png',
      items: [
        'Security architecture & defensive strategies',
        'Managed Detection & Response (24/7 SOC)',
        'Penetration testing and security audits',
        'Social engineering simulations',
        'SIEM, PAM, IAM implementation',
        'Incident readiness and threat detection'
      ]
    },
    {
      title: 'Business Applications',
      bigTitle: 'Transform processes through digital solutions',
      description: 'Beyond infrastructure, we support business transformation through ERP/CRM integration and intelligent process automation.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80',
      items: [
        'ERP and CRM system integration',
        'Process automation and optimization',
        'Custom application implementation',
        'Digital transformation consulting',
        'System customization & maintenance',
        'Data-driven business intelligence'
      ]
    },
    {
      title: 'Consulting & Training',
      bigTitle: 'Empower your teams with strategic expertise',
      description: 'We act as a strategic partner, upskilling teams and providing technology advisory to ensure you stay ahead of the curve.',
      image: '/images/Server administration 6.png',
      items: [
        'IT and cybersecurity consulting',
        'Architecture design and advisory',
        'Tailored training programs',
        'Strategic technology roadmapping',
        'Compliance & Risk Advisory',
        'Digital strategy development'
      ]
    }
  ];

  return (
    <div className="bg-white text-gray-900 overflow-hidden">
      {/* 1. PREMIUM HERO SECTION */}
      <section className="relative min-h-[70vh] flex items-center pt-20 overflow-hidden bg-white border-b border-gray-100">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-50/50 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-indigo-50/30 rounded-full blur-[100px]"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <h1 className="text-5xl lg:text-7xl font-light mb-8 leading-tight tracking-tight text-gray-900">
                About <span className="text-[#0056D2] font-bold">Atlas Defenders</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-xl mb-10 font-normal">
                We are a global cybersecurity and IT infrastructure leader committed to building, securing, and optimizing digital ecosystems. Our mission is to deliver resilient, high-performance environments that protect and empower modern organizations.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="relative lg:flex lg:justify-end"
            >
              <div className="relative z-10 w-full max-w-[500px] flex items-center justify-center lg:justify-end">
                <img 
                  src="/Logos/atlasdefenders_blue_logo.svg" 
                  alt="Atlas Defenders" 
                  className="w-full h-auto drop-shadow-[0_20px_50px_rgba(0,86,210,0.1)] hover:scale-[1.02] transition-transform duration-700 select-none grayscale-[0.2] hover:grayscale-0"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-4xl md:text-5xl font-normal text-gray-900 mb-8 leading-tight">
              "Resilience is not optional, <span className="text-[#0056D2] font-semibold italic">it's essential."</span>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-12">
              Our mission is to empower businesses with resilient infrastructures and strong cybersecurity foundations, 
              enabling them to operate confidently in a rapidly evolving digital landscape. We combine advanced technical 
              expertise with a strategic consulting mindset to deliver reliable, secure, and scalable solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What We Do Section - ServiceNow Style */}
      <section className="py-24 bg-[#f8fafc] border-y border-gray-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] text-[#0056D2] uppercase mb-4 block">
              Service Capabilities
            </span>
            <h2 className="text-4xl md:text-5xl font-normal text-gray-900">What We Do</h2>
          </div>

          <div className="max-w-6xl mx-auto">
            {/* 1. Category Row Selectors */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 relative z-20">
              {serviceCategories.map((service, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTab(index)}
                  className={`relative p-6 text-left transition-all duration-500 border-x border-t flex flex-col justify-between h-[110px] ${
                    activeTab === index 
                      ? 'bg-white border-gray-200 z-30 shadow-[0_-8px_20px_rgba(0,0,0,0.03)] rounded-t-2xl' 
                      : 'bg-gray-50/50 border-gray-100/50 hover:border-gray-200 text-gray-400 group h-[100px] shadow-sm rounded-t-xl mt-auto'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <span className={`font-bold text-lg transition-colors leading-tight ${
                      activeTab === index ? 'text-gray-900' : 'text-gray-400 group-hover:text-gray-600'
                    }`}>
                      {service.title}
                    </span>
                  </div>
                  <div className="flex justify-end">
                    {activeTab === index ? (
                      <Minus className="w-5 h-5 text-gray-900" />
                    ) : (
                      <Plus className="w-5 h-5 text-gray-300 group-hover:text-[#0056D2]" />
                    )}
                  </div>
                  {/* Seamless linking border overlay */}
                  {activeTab === index && (
                    <div className="absolute -bottom-[2px] left-0 right-0 h-[4px] bg-white z-40"></div>
                  )}
                </button>
              ))}
            </div>

            {/* 2. Expanded Content Pane */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
                className={`bg-white border border-gray-200 p-8 lg:p-14 shadow-2xl overflow-hidden relative z-10 -mt-[1px] ${
                  activeTab === 0 ? 'rounded-b-[2.5rem] rounded-tr-[2.5rem]' : 
                  activeTab === 3 ? 'rounded-b-[2.5rem] rounded-tl-[2.5rem]' :
                  'rounded-b-[2.5rem] rounded-t-[2.5rem]'
                }`}
              >
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  <div>
                    <h4 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                      {serviceCategories[activeTab].bigTitle}
                    </h4>
                    <p className="text-lg text-gray-500 mb-8 leading-relaxed font-normal">
                      {serviceCategories[activeTab].description}
                    </p>
                    
                    <div className="space-y-8">
                      <div>
                        <h5 className="text-[#0056D2] font-bold uppercase tracking-widest text-xs mb-4 mt-1">Core Offerings</h5>
                        <div className="grid sm:grid-cols-2 gap-4">
                          {serviceCategories[activeTab].items.map((item, i) => (
                            <Link 
                              key={i} 
                              to="#" 
                              className="text-gray-700 hover:text-[#0056D2] font-medium text-sm flex items-center group/item transition-colors"
                            >
                              <div className="w-1.5 h-1.5 bg-[#0056D2] rounded-full mr-3 group-hover/item:scale-150 transition-transform"></div>
                              {item}
                            </Link>
                          ))}
                        </div>
                      </div>
                      
                      <div className="pt-8 border-t border-gray-100">
                         <Link to="/contact" className="inline-flex items-center space-x-2 text-[#0056D2] font-bold group">
                            <span>Get Started with {serviceCategories[activeTab].title}</span>
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                         </Link>
                      </div>
                    </div>
                  </div>

                  <div className="relative">
                    <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
                      <img 
                        src={serviceCategories[activeTab].image} 
                        alt={serviceCategories[activeTab].title}
                        className="w-full h-[400px] object-cover hover:scale-105 transition-transform duration-700" 
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* 4. OUR VISION SECTION - Keep this premium & dark */}
      <section className="py-24 relative overflow-hidden bg-[#020617] text-white">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-20">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:w-1/2"
            >
              <div className="text-[#0056D2] font-black text-sm uppercase tracking-widest mb-6 mt-1">Long-Term Partnerships</div>
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight">Our Vision</h2>
              <p className="text-lg text-gray-400 leading-relaxed mb-8 font-light">
                At Atlas Defenders, we go beyond delivering technology—we build long-term partnerships. Our vision is to become a trusted leader in cybersecurity and IT services, helping organizations not only protect their assets but also leverage technology as a driver of growth and innovation.
              </p>
              <div className="space-y-4">
                {[
                  'Trusted leadership in cybersecurity',
                  'Leveraging technology for growth',
                  'Building secure, future-ready ecosystems',
                  'Global standards with local expertise'
                ].map((point, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-5 h-5 rounded-full bg-[#0056D2]/20 flex items-center justify-center">
                      <CheckCircle2 className="w-3 h-3 text-[#0056D2]" />
                    </div>
                    <span className="text-gray-300 text-sm font-light opacity-90 leading-none mt-1">{point}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="lg:w-1/2 relative"
            >
              <div className="relative z-10 grid grid-cols-2 gap-4">
                <div className="space-y-4 pt-12">
                  <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                    <img src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=400&q=80" alt="Datacenter" className="w-full h-auto opacity-80" />
                  </div>
                  <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                    <img src="https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=400&q=80" alt="Network" className="w-full h-auto opacity-80" />
                  </div>
                </div>
                <div className="space-y-4">
                   <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                    <img src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=400&q=80" alt="Tech" className="w-full h-auto opacity-80" />
                  </div>
                  <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                    <img src="https://images.unsplash.com/photo-1518709268805-4e9042af2176?auto=format&fit=crop&w=400&q=80" alt="Security" className="w-full h-auto opacity-80" />
                  </div>
                </div>
              </div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-[#0056D2]/10 rounded-full blur-[80px] pointer-events-none"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 5. CTA - REDESIGNED ON WHITE BACKGROUND */}
      <section className="py-24 relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-blue-50/50 backdrop-blur-3xl pointer-events-none"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="bg-gradient-to-br from-blue-900 via-blue-950 to-slate-900 p-12 lg:p-20 rounded-[3rem] border border-white/10 text-center max-w-6xl mx-auto shadow-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl lg:text-6xl font-bold text-white mb-8">Ready to Build Your Future?</h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-12 font-light">
                Join forces with Atlas Defenders to protect your assets and drive your business forward with resilient, state-of-the-art infrastructure.
              </p>
              <Link
                to="/contact"
                className="group relative inline-flex items-center justify-center bg-[#0056D2] hover:bg-[#004bb8] text-white px-10 py-5 rounded-2xl font-bold text-lg transition-all duration-300 shadow-2xl hover:shadow-[#0056D2]/25 hover:scale-105 active:scale-95 overflow-hidden"
              >
                <span className="relative z-10 flex items-center">
                   Start Your Consultation
                  <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;